/** Monta a tabela “Em aberto” do mock a partir de GET /api/orders/today-route. */

import { getBoardClienteExibicao, getBoardClienteTooltipNome } from '../../board/utils/clienteBoardDisplay';
import { defeitoTerm } from '../../pg-native/useCutoverActive';

const DEFECT_ICON =
  '<svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true"><path d="M12 9v5"/><path d="M12 17.5v.5"/><path d="M10.3 3.9L2.6 18a1.6 1.6 0 0 0 1.4 2.4h16a1.6 1.6 0 0 0 1.4-2.4L13.7 3.9a1.6 1.6 0 0 0-2.8 0z"/></svg>';

const PEDIDO_ICON =
  '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><path d="M3 7l9-4 9 4-9 4-9-4z"/><path d="M3 7v10l9 4 9-4V7"/></svg>';

function text(el, value) {
  el.textContent = value == null || value === '' ? '' : String(value);
}

function formatPrevisao(val) {
  if (val == null || val === '') return { label: '—', color: '#94a3b8' };
  const s = String(val);
  if (/^\d{2}\/\d{2}/.test(s)) {
    return { label: s.slice(0, 16), color: '#64748b' };
  }
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return { label: s, color: '#64748b' };

  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startTomorrow = new Date(startToday);
  startTomorrow.setDate(startTomorrow.getDate() + 1);
  const startDayAfter = new Date(startToday);
  startDayAfter.setDate(startDayAfter.getDate() + 2);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const time = `${hh}:${mm}`;

  if (d >= startToday && d < startTomorrow) {
    const overdue = d.getTime() < now.getTime();
    return {
      label: `SLA hoje ${time}`,
      color: overdue ? '#d1443e' : '#c27803',
    };
  }
  if (d >= startTomorrow && d < startDayAfter) {
    return { label: `SLA amanhã ${time}`, color: '#2563eb' };
  }
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return {
    label: `${day}/${month}/${year} ${time}`,
    color: '#64748b',
  };
}

function orderSeq(ordem) {
  const n = Number(ordem?.TB02115_ORDEM);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

function sortOpenOrders(orders) {
  return [...(orders || [])].sort((a, b) => {
    const oa = orderSeq(a) || 0;
    const ob = orderSeq(b) || 0;
    if (oa === 0 && ob === 0) return 0;
    if (oa === 0) return 1;
    if (ob === 0) return -1;
    return oa - ob;
  });
}

export function suggestNextSequence(orders) {
  let max = 0;
  (orders || []).forEach((o) => {
    const n = orderSeq(o);
    if (n != null && n > max) max = n;
  });
  return max + 1;
}

/**
 * @param {HTMLElement} root
 * @param {object[]} orders
 * @param {{
 *   editingOs?: string|null,
 *   sequenceValue?: string,
 *   isSaving?: boolean,
 *   onStartRoteirizar?: (osCode: string, currentSeq: number|null) => void,
 *   onRemoveRoteiro?: (osCode: string) => void,
 *   onSequenceChange?: (value: string) => void,
 *   onSaveSequence?: (osCode: string) => void,
 *   onCancelSequence?: () => void,
 * }} opts
 */
export function applyTechnicianPanelOpenToMockDom(root, orders, opts = {}) {
  if (!root) return;

  const {
    editingOs = null,
    sequenceValue = '',
    isSaving = false,
    onStartRoteirizar,
    onRemoveRoteiro,
    onSequenceChange,
    onSaveSequence,
    onCancelSequence,
  } = opts;

  const list = sortOpenOrders(orders);
  const hasRouted = list.some((o) => orderSeq(o) != null);

  const countLabel = root.querySelector('[data-tpm="open-count-label"]');
  if (countLabel) {
    text(countLabel, `${list.length} OS em aberto`);
  }
  const bannerSub = root.querySelector('.tpm-open-banner-sub');
  if (bannerSub) {
    text(
      bannerSub,
      hasRouted
        ? 'roteiro do dia — defina a numeração ou reordene'
        : 'nenhuma roteirizada — defina a ordem para fechar o dia'
    );
  }
  const openTabCount = root.querySelector('[data-tab="open"] .tpm-count');
  if (openTabCount) text(openTabCount, String(list.length));

  const inner = root.querySelector('.tpm-open-table-inner');
  if (!inner) return;

  inner.querySelectorAll('[data-tpm="open-row"], [data-tpm="open-empty"]').forEach((el) => el.remove());

  if (list.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'tpm-open-empty';
    empty.setAttribute('data-tpm', 'open-empty');
    text(empty, 'Nenhuma ordem em aberto');
    inner.appendChild(empty);
    return;
  }

  const frag = document.createDocumentFragment();

  list.forEach((o) => {
    const os = String(o.TB02115_CODIGO ?? '').trim();
    if (!os) return;
    const seq = orderSeq(o);
    const cliente = getBoardClienteExibicao(o) || '—';
    const clienteTip = getBoardClienteTooltipNome(o) || cliente;
    const equip = o.TB01010_RESUMIDO || o.TB01010_NOME || '—';
    const serie = o.TB02115_NUMSERIE || '—';
    const pat = o.TB02112_PAT ? `pat. ${o.TB02112_PAT}` : null;
    const pedido = o.PEDIDO_VINCULADO || o.pedidoVinculado || null;
    const cidade = o.TB02115_CIDADE || '—';
    const bairro = o.TB02115_BAIRRO || '—';
    const defectTitle = o.defeito_descricao || o.TB01048_NOME || o.defeito || '';
    const defectNote = o.TB02116_OBS || o.TB02115_OBS || '';
    const sla = formatPrevisao(o.CALC_PREVISAO);
    const isEditing = editingOs != null && String(editingOs) === os;

    const row = document.createElement('div');
    row.className = 'tpm-open-row';
    row.setAttribute('data-tpm', 'open-row');
    row.setAttribute('data-os', os);
    if (seq != null) row.classList.add('is-routed');

    const osCell = document.createElement('span');
    osCell.className = 'tpm-open-os';
    if (seq != null) {
      const badge = document.createElement('span');
      badge.className = 'tpm-open-seq';
      badge.setAttribute('data-tpm-tip', 'Ordem no roteiro');
      text(badge, String(seq));
      osCell.appendChild(badge);
    }
    const osCode = document.createElement('span');
    text(osCode, `#${os}`);
    osCell.appendChild(osCode);
    const returns = Number(o.returnCount) || 0;
    if (returns > 0) {
      const ret = document.createElement('span');
      ret.className = 'tpm-return-badge';
      ret.setAttribute('data-tpm-tip', `Retorno ${returns}${o.lastReturnCondition ? ` · ${o.lastReturnCondition}` : ''}`);
      text(ret, `Retorno ${returns}`);
      osCell.appendChild(ret);
    }
    row.appendChild(osCell);

    const pedidoCell = document.createElement('span');
    pedidoCell.className = 'tpm-open-pedido';
    if (pedido) {
      const chip = document.createElement('span');
      chip.className = 'tpm-open-order';
      chip.setAttribute('data-tpm-tip', `Pedido de peças ${pedido} — levar junto com a OS`);
      chip.setAttribute('data-tpm-tip-wrap', '');
      chip.innerHTML = PEDIDO_ICON;
      chip.appendChild(document.createTextNode(String(pedido)));
      pedidoCell.appendChild(chip);
    } else {
      const empty = document.createElement('span');
      empty.className = 'tpm-open-order-empty';
      text(empty, '—');
      pedidoCell.appendChild(empty);
    }
    row.appendChild(pedidoCell);

    const client = document.createElement('span');
    client.className = 'tpm-open-client';
    if (clienteTip) {
      client.setAttribute('data-tpm-tip', clienteTip);
      if (String(clienteTip).length > 36) client.setAttribute('data-tpm-tip-wrap', '');
    }
    text(client, cliente);
    row.appendChild(client);

    const equipCell = document.createElement('span');
    equipCell.className = 'tpm-open-equip';
    const equipTop = document.createElement('span');
    equipTop.className = 'tpm-open-equip-top';
    const equipName = document.createElement('span');
    equipName.className = 'tpm-open-equip-name';
    if (equip) {
      equipName.setAttribute('data-tpm-tip', String(equip));
      if (String(equip).length > 36) equipName.setAttribute('data-tpm-tip-wrap', '');
    }
    text(equipName, equip);
    equipTop.appendChild(equipName);

    if (defectTitle) {
      const defectBtn = document.createElement('button');
      defectBtn.type = 'button';
      defectBtn.className = 'tpm-open-defect-btn';
      defectBtn.setAttribute('data-tpm', 'open-defect');
      defectBtn.setAttribute('data-os', os);
      const term = defeitoTerm();
      defectBtn.setAttribute('data-tpm-tip', term === 'Serviços' ? 'Ver observações dos serviços' : 'Ver observações do defeito');
      defectBtn.innerHTML = `${DEFECT_ICON}${term.toLowerCase()}`;
      equipTop.appendChild(defectBtn);
    }
    equipCell.appendChild(equipTop);

    const meta = document.createElement('span');
    meta.className = 'tpm-open-equip-meta';
    const serieEl = document.createElement('span');
    text(serieEl, serie);
    meta.appendChild(serieEl);
    if (pat) {
      const sep = document.createElement('span');
      text(sep, '·');
      meta.appendChild(sep);
      const patEl = document.createElement('span');
      patEl.setAttribute('data-tpm-tip', 'Patrimônio');
      text(patEl, pat);
      meta.appendChild(patEl);
    }
    equipCell.appendChild(meta);

    if (defectTitle) {
      const pop = document.createElement('span');
      pop.className = 'tpm-open-defect-pop';
      pop.setAttribute('data-tpm', 'open-defect-pop');
      pop.setAttribute('data-os', os);
      pop.hidden = true;
      const popH = document.createElement('span');
      popH.className = 'tpm-open-defect-pop-h';
      const popTitle = document.createElement('span');
      text(popTitle, defeitoTerm() === 'Serviços' ? 'Serviço relatado' : 'Defeito relatado');
      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.setAttribute('data-tpm', 'open-defect-close');
      closeBtn.setAttribute('data-os', os);
      closeBtn.setAttribute('aria-label', 'Fechar');
      text(closeBtn, '✕');
      popH.appendChild(popTitle);
      popH.appendChild(closeBtn);
      pop.appendChild(popH);
      const titleEl = document.createElement('span');
      titleEl.className = 'tpm-open-defect-title';
      text(titleEl, defectTitle);
      pop.appendChild(titleEl);
      if (defectNote) {
        const noteEl = document.createElement('span');
        noteEl.className = 'tpm-open-defect-note';
        text(noteEl, defectNote);
        pop.appendChild(noteEl);
      }
      equipCell.appendChild(pop);
    }
    row.appendChild(equipCell);

    const city = document.createElement('span');
    city.className = 'tpm-open-city';
    const cityName = document.createElement('span');
    text(cityName, cidade);
    const bairroEl = document.createElement('span');
    text(bairroEl, bairro);
    city.appendChild(cityName);
    city.appendChild(bairroEl);
    row.appendChild(city);

    const slaEl = document.createElement('span');
    slaEl.className = 'tpm-open-sla';
    slaEl.style.color = sla.color;
    const dot = document.createElement('span');
    dot.className = 'tpm-open-sla-dot';
    dot.style.background = sla.color;
    slaEl.appendChild(dot);
    slaEl.appendChild(document.createTextNode(sla.label));
    row.appendChild(slaEl);

    if (isEditing) {
      const wrap = document.createElement('div');
      wrap.className = 'tpm-open-seq-edit';
      const input = document.createElement('input');
      input.type = 'number';
      input.min = '1';
      input.className = 'tpm-open-seq-input';
      input.placeholder = 'Nº';
      input.value = sequenceValue ?? '';
      input.disabled = isSaving;
      input.setAttribute('aria-label', 'Número da ordem no roteiro');
      input.addEventListener('click', (e) => e.stopPropagation());
      input.addEventListener('input', (e) => onSequenceChange?.(e.target.value));
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (!isSaving) onSaveSequence?.(os);
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          if (!isSaving) onCancelSequence?.();
        }
      });
      const ok = document.createElement('button');
      ok.type = 'button';
      ok.className = 'tpm-open-seq-ok';
      ok.disabled = isSaving;
      ok.setAttribute('data-tpm-tip', isSaving ? 'Salvando…' : 'Confirmar ordem');
      text(ok, isSaving ? '…' : '✓');
      ok.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isSaving) onSaveSequence?.(os);
      });
      const cancel = document.createElement('button');
      cancel.type = 'button';
      cancel.className = 'tpm-open-seq-cancel';
      cancel.disabled = isSaving;
      cancel.setAttribute('data-tpm-tip', 'Cancelar');
      text(cancel, '×');
      cancel.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isSaving) onCancelSequence?.();
      });
      wrap.appendChild(input);
      wrap.appendChild(ok);
      wrap.appendChild(cancel);
      row.appendChild(wrap);
      queueMicrotask(() => {
        try {
          input.focus();
          input.select();
        } catch {
          /* ignore */
        }
      });
    } else {
      const actions = document.createElement('div');
      actions.className = 'tpm-open-actions';

      const action = document.createElement('button');
      action.type = 'button';
      action.className = 'tpm-open-action';
      action.setAttribute('data-tpm', 'open-roteirizar');
      action.setAttribute('data-os', os);
      action.disabled = isSaving;
      text(action, seq != null ? 'Alterar ordem' : 'Roteirizar');
      action.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        onStartRoteirizar?.(os, seq);
      });
      actions.appendChild(action);

      if (seq != null) {
        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'tpm-open-action tpm-open-action--remove';
        removeBtn.setAttribute('data-tpm', 'open-remover');
        removeBtn.setAttribute('data-os', os);
        removeBtn.disabled = isSaving;
        removeBtn.setAttribute('data-tpm-tip', 'Remover do roteiro');
        text(removeBtn, 'Remover');
        removeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          onRemoveRoteiro?.(os);
        });
        actions.appendChild(removeBtn);
      }

      row.appendChild(actions);
    }

    frag.appendChild(row);
  });

  inner.appendChild(frag);
}
